import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2, Pencil, Plus, Trash2, Loader2 } from "lucide-react";

import { api, type Tenant } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import { PageHeader } from "@/components/page-header";
import { ListFilter } from "@/components/list-filter";
import { AdminListLayout, AdminFilterPanel, AdminFilterGroup } from "@/components/admin-filter-panel";
import { ListPagination, ListSearch, ListSort, useBrowsingState, usePagedList } from "@/components/list-controls";
import { useDialogDraft } from "@/components/dialog-draft";

const tenantSchema = z.object({
  name: z.string().min(1, "Name is required").max(120),
  external_key: z
    .string()
    .min(1, "External key is required")
    .max(120)
    .regex(/^[a-z0-9-]+$/, "Lowercase letters, digits, and hyphens only"),
  kind: z.enum(["vendor", "distribution_company"]),
});
type TenantValues = z.infer<typeof tenantSchema>;

export function CompaniesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [deleting, setDeleting] = useState<Tenant | null>(null);
  const [kind, setKind] = useBrowsingState("organizations:kind", "");
  const createDraft = useDialogDraft();
  const editDraft = useDialogDraft();
  const resetCreateDraft = createDraft.reset;
  const resetEditDraft = editDraft.reset;
  useEffect(() => {
    if (!createOpen) resetCreateDraft();
  }, [createOpen, resetCreateDraft]);
  useEffect(() => {
    if (editing === null) resetEditDraft();
  }, [editing, resetEditDraft]);
  const { list, query: tenantsQuery } = usePagedList("tenants", { kind }, [
    { key: "name", label: "Name" }, { key: "external_key", label: "External key" },
    { key: "kind", label: "Kind" },
  ], api.browseTenants);

  const createMutation = useMutation({
    mutationFn: (body: TenantValues) => api.createTenant(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setCreateOpen(false);
      createDraft.reset();
      toast({ title: "Tenant created", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Create failed",
        description: error instanceof Error ? error.message : "Unknown",
        variant: "destructive",
      }),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { tenantId: number; body: TenantValues }) =>
      api.updateTenant(input.tenantId, input.body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setEditing(null);
      editDraft.reset();
      toast({ title: "Tenant updated", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "Unknown",
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (tenantId: number) => api.deleteTenant(tenantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setDeleting(null);
      toast({ title: "Tenant deleted", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Unknown",
        variant: "destructive",
      }),
  });

  return (
    <>
      <PageHeader
        title="Organizations"
        description="Vendors own controller fleets. Distribution companies view explicitly shared data."
        actions={
          <Dialog open={createOpen} onOpenChange={(open) => {
            if (open) { createDraft.reset(); setCreateOpen(true); }
            else if (createDraft.canClose(createMutation.isPending)) setCreateOpen(false);
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4" /> New organization
              </Button>
            </DialogTrigger>
            <DialogContent onChangeCapture={createDraft.onChangeCapture} onClickCapture={createDraft.onClickCapture} onKeyDownCapture={createDraft.onKeyDownCapture}>
              <TenantForm
                title="Create organization"
                description="Choose its platform role before adding users or substations."
                submitting={createMutation.isPending}
                submitError={createMutation.error instanceof Error ? createMutation.error.message : null}
                onCancel={() => { if (createDraft.canClose(createMutation.isPending)) setCreateOpen(false); }}
                onSubmit={(values) => createMutation.mutate(values)}
              />
            </DialogContent>
          </Dialog>
        }
      />

      {tenantsQuery.error ? (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {tenantsQuery.error instanceof Error ? tenantsQuery.error.message : "Failed to load"}
        </div>
      ) : null}

      <AdminListLayout filters={
      <AdminFilterPanel count={Number(Boolean(kind)) + Number(Boolean(list.search.trim()))} onClear={() => { setKind(""); list.setSearch(""); }}>
        <AdminFilterGroup label="Organization">
        <ListFilter label="Organization kind" value={kind} options={[
          { value: "vendor", label: "Vendor" },
          { value: "distribution_company", label: "Distribution company" },
        ]} onChange={(value) => { setKind(value); list.setPage(1); }} />
        </AdminFilterGroup>
        <AdminFilterGroup label="Search">
        <ListSearch
          label="Search organizations"
          placeholder="Search organizations, kinds, external keys..."
          value={list.search}
          onChange={list.setSearch}
        />
        <ListSort {...list} />
        </AdminFilterGroup>
      </AdminFilterPanel>
      }>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Organization</th>
                <th className="px-4 py-3">Kind</th>
                <th className="px-4 py-3">External key</th>
                <th className="px-4 py-3 text-right">ID</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenantsQuery.isLoading ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <tr key={idx} className="border-b border-border">
                    <td className="px-4 py-3" colSpan={5}>
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))
              ) : tenantsQuery.error && !tenantsQuery.data ? (
                <tr><td className="px-4 py-8 text-center" colSpan={5}>
                  Unable to load organizations.{" "}
                  <Button variant="outline" size="sm" onClick={() => void tenantsQuery.refetch()}>Retry</Button>
                </td></tr>
              ) : list.total > 0 ? (
                list.rows.map((tenant) => (
                  <tr key={tenant.tenant_id} className="border-b border-border last:border-0 hover:bg-accent/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{tenant.name}</div>
                          <div className="text-xs text-muted-foreground">Organization #{tenant.tenant_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {tenant.kind === "vendor" ? "Vendor" : "Distribution company"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground">
                      {tenant.external_key}
                    </td>
                    <td className="px-4 py-3 text-right tabular text-xs text-muted-foreground">
                      {tenant.tenant_id}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => { editDraft.reset(); setEditing(tenant); }}>
                          <Pencil className="h-4 w-4" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleting(tenant)}
                        >
                          <Trash2 className="h-4 w-4" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-8 text-center text-muted-foreground" colSpan={5}>
                    {kind || list.search.trim()
                      ? "No organizations match the current filter."
                      : "No organizations yet. Create a vendor or distribution company."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
        <ListPagination {...list} />
      </Card>
      </AdminListLayout>

      {/* Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(open) => { if (!open && editDraft.canClose(updateMutation.isPending)) setEditing(null); }}>
        <DialogContent onChangeCapture={editDraft.onChangeCapture} onClickCapture={editDraft.onClickCapture} onKeyDownCapture={editDraft.onKeyDownCapture}>
          {editing ? (
            <TenantForm
              title={`Edit ${editing.name}`}
              description="Rename, rekey, or change the organization kind."
              defaults={{ name: editing.name, external_key: editing.external_key, kind: editing.kind }}
              submitting={updateMutation.isPending}
              submitError={updateMutation.error instanceof Error ? updateMutation.error.message : null}
              onCancel={() => { if (editDraft.canClose(updateMutation.isPending)) setEditing(null); }}
              onSubmit={(values) => updateMutation.mutate({ tenantId: editing.tenant_id, body: values })}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
          <DialogTitle>Delete organization?</DialogTitle>
            <DialogDescription>
            This removes organization{" "}
              <span className="font-semibold text-foreground">{deleting?.name}</span>. Devices and
              users linked to it will be orphaned until reassigned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleting && deleteMutation.mutate(deleting.tenant_id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type TenantFormProps = {
  title: string;
  description?: string;
  defaults?: TenantValues;
  submitting: boolean;
  submitError: string | null;
  onCancel: () => void;
  onSubmit: (values: TenantValues) => void;
};

function TenantForm({ title, description, defaults, submitting, submitError, onCancel, onSubmit }: TenantFormProps) {
  const [review, setReview] = useState<TenantValues | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TenantValues>({
    resolver: zodResolver(tenantSchema),
    defaultValues: defaults ?? { name: "", external_key: "", kind: "vendor" },
  });

  return (
    <form onChangeCapture={() => setReview(null)} onSubmit={handleSubmit((values) => {
      if (submitting) return;
      if (defaults && !review) setReview(values);
      else onSubmit(values);
    })} className="space-y-4" noValidate>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description ? <DialogDescription>{description}</DialogDescription> : null}
      </DialogHeader>
      {submitError ? <p role="alert" className="text-sm text-destructive">{submitError}</p> : null}
      {review && defaults ? <section aria-label="Review changes" className="rounded-md border p-3 text-sm">
        <h3 className="font-semibold">Review changes</h3>
        {(["name", "external_key", "kind"] as const).filter((field) => defaults[field] !== review[field]).map((field) =>
          <p key={field}>{field.replaceAll("_", " ")}: {defaults[field]} → {review[field]}</p>)}
        {(["name", "external_key", "kind"] as const).every((field) => defaults[field] === review[field]) ? <p>No changes.</p> : null}
      </section> : null}
      <div className="space-y-2">
        <Label>Organization kind</Label>
        <Select value={watch("kind")} onValueChange={(value) => { setReview(null); setValue("kind", value as TenantValues["kind"]); }}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="vendor">Vendor</SelectItem>
            <SelectItem value="distribution_company">Distribution company</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input id="name" placeholder="Northline Utility" {...register("name")} />
        {errors.name ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="external_key">External key</Label>
        <Input id="external_key" placeholder="northline-utility" {...register("external_key")} />
        <p className="text-xs text-muted-foreground">
          URL-safe slug used to reference the organization in scripts and audit logs.
        </p>
        {errors.external_key ? (
          <p className="text-xs text-destructive">{errors.external_key.message}</p>
        ) : null}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        {review ? <Button type="button" variant="outline" onClick={() => setReview(null)}>Back to edit</Button> : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {defaults && !review ? "Review changes" : "Save"}
        </Button>
      </DialogFooter>
    </form>
  );
}
